/**
 * ApiKeysTab Component
 * Centralized API key vault management tab
 * Design System compliant (DESIGN_SYSTEM.md)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  IconKey,
  IconPlus,
  IconTrash,
  IconEdit,
  IconCheck,
  IconX,
  IconEye,
  IconEyeOff,
  IconBrain,
  IconSparkles,
  IconBrandGoogle,
  IconBolt,
  IconShieldLock,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import AgentsFlowService from '../../services/AgentsFlowService';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import './ApiKeysTab.css';

// ===============================================
// PROVIDER CONFIG
// ===============================================

const PROVIDER_CONFIG = {
  openai: {
    name: 'OpenAI',
    icon: IconBrain,
    color: '#10a37f',
    gradient: 'linear-gradient(135deg, #10a37f 0%, #0d8a6a 100%)',
    lightBg: '#ecfdf5',
  },
  anthropic: {
    name: 'Anthropic',
    icon: IconSparkles,
    color: '#d97706',
    gradient: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
    lightBg: '#fffbeb',
  },
  google: {
    name: 'Google',
    icon: IconBrandGoogle,
    color: '#4285f4',
    gradient: 'linear-gradient(135deg, #4285f4 0%, #1a73e8 100%)',
    lightBg: '#eff6ff',
  }
};

const MAX_KEYS = 10;

// ===============================================
// VAULT KEY ITEM
// ===============================================

const VaultKeyItem = ({ entry, onDelete, onEditLabel }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(entry.label);

  const handleSave = () => {
    if (editValue.trim()) {
      onEditLabel(entry.id, editValue.trim());
      setEditing(false);
    }
  };

  return (
    <div className="akv-item">
      {editing ? (
        <div className="akv-item__edit">
          <input
            type="text"
            className="akv-item__edit-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') setEditing(false);
            }}
          />
          <button className="akv-item__btn akv-item__btn--confirm" onClick={handleSave}>
            <IconCheck size={14} />
          </button>
          <button className="akv-item__btn" onClick={() => setEditing(false)}>
            <IconX size={14} />
          </button>
        </div>
      ) : (
        <>
          <div className="akv-item__info">
            <span className="akv-item__name">{entry.label}</span>
            <span className="akv-item__key">{entry.key}</span>
          </div>
          <div className="akv-item__actions">
            <button
              className="akv-item__btn"
              onClick={() => { setEditing(true); setEditValue(entry.label); }}
              title="Editar label"
            >
              <IconEdit size={18} />
            </button>
            <button
              className="akv-item__btn akv-item__btn--danger"
              onClick={() => onDelete(entry.id, entry.label)}
              title="Excluir"
            >
              <IconTrash size={18} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ===============================================
// ADD KEY FORM
// ===============================================

const AddKeyForm = ({ onAdd, onCancel }) => {
  const [form, setForm] = useState({ label: '', provider: 'openai', key: '' });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.label.trim() || !form.key.trim()) return;
    setSaving(true);
    try {
      await onAdd({
        label: form.label.trim(),
        provider: form.provider,
        key: form.key.trim(),
      });
      setForm({ label: '', provider: 'openai', key: '' });
      setShowKey(false);
    } catch (err) {
      // error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="akv-add">
      <div className="akv-add__header">
        <h4 className="akv-add__title">Adicionar nova chave</h4>
        <button className="akv-item__btn" onClick={onCancel}>
          <IconX size={18} />
        </button>
      </div>

      <div className="akv-add__field">
        <label className="akv-add__label">Provedor</label>
        <div className="akv-add__providers">
          {Object.entries(PROVIDER_CONFIG).map(([id, cfg]) => {
            const Icon = cfg.icon;
            const isSelected = form.provider === id;
            return (
              <button
                key={id}
                className={`akv-add__provider ${isSelected ? 'akv-add__provider--active' : ''}`}
                style={isSelected ? { '--provider-color': cfg.color, '--provider-bg': cfg.lightBg } : {}}
                onClick={() => setForm({ ...form, provider: id })}
              >
                <div
                  className="akv-add__provider-icon"
                  style={isSelected ? { background: cfg.gradient } : {}}
                >
                  <Icon size={16} />
                </div>
                <span>{cfg.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="akv-add__field">
        <label className="akv-add__label">Label</label>
        <input
          type="text"
          className="akv-add__input"
          placeholder="Ex: OpenAI Produção"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
      </div>

      <div className="akv-add__field">
        <label className="akv-add__label">API Key</label>
        <div className="akv-add__key-wrapper">
          <input
            type={showKey ? 'text' : 'password'}
            className="akv-add__input"
            placeholder="sk-..."
            value={form.key}
            onChange={(e) => setForm({ ...form, key: e.target.value })}
          />
          <button
            type="button"
            className="akv-add__key-toggle"
            onClick={() => setShowKey(!showKey)}
          >
            {showKey ? <IconEyeOff size={16} /> : <IconEye size={16} />}
          </button>
        </div>
      </div>

      <div className="akv-add__footer">
        <button className="akv-btn akv-btn--secondary" onClick={onCancel}>
          Cancelar
        </button>
        <button
          className="akv-btn akv-btn--primary"
          onClick={handleSubmit}
          disabled={saving || !form.label.trim() || !form.key.trim()}
        >
          {saving ? <Spinner animation="border" size="sm" /> : <IconCheck size={16} />}
          Salvar chave
        </button>
      </div>
    </div>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const ApiKeysTab = () => {
  const { state, setVaultKeys } = useAgentsFlow();
  const { vaultKeys } = state;
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      const res = await AgentsFlowService.getVaultKeys();
      setVaultKeys(res.data?.vault || []);
    } catch (err) {
      console.error('Error loading vault keys:', err);
    } finally {
      setLoading(false);
    }
  }, [setVaultKeys]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleAdd = useCallback(async (data) => {
    try {
      await AgentsFlowService.addVaultKey(data);
      await loadKeys();
      setShowAddForm(false);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao adicionar chave';
      Swal.fire({ icon: 'error', title: 'Erro', text: msg });
      throw err;
    }
  }, [loadKeys]);

  const handleDelete = useCallback(async (keyId, label) => {
    const result = await Swal.fire({
      title: 'Excluir chave?',
      text: `A chave "${label}" será removida do cofre.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;

    try {
      await AgentsFlowService.deleteVaultKey(keyId);
      await loadKeys();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: 'Erro ao excluir chave' });
    }
  }, [loadKeys]);

  const handleEditLabel = useCallback(async (keyId, newLabel) => {
    try {
      await AgentsFlowService.updateVaultKey(keyId, { label: newLabel });
      await loadKeys();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: 'Erro ao atualizar label' });
    }
  }, [loadKeys]);

  // Group keys by provider
  const groupedKeys = {};
  vaultKeys.forEach((k) => {
    if (!groupedKeys[k.provider]) groupedKeys[k.provider] = [];
    groupedKeys[k.provider].push(k);
  });

  return (
    <div className="akv-config">
      {/* Header */}
      <section className="akv-section">
        <div className="akv-section__header">
          <h3 className="akv-section__title">Cofre de API Keys</h3>
          <span className="akv-section__subtitle">
            Gerencie suas chaves de forma centralizada. As chaves são criptografadas e compartilhadas entre todos os seus agentes.
          </span>
        </div>

        {loading ? (
          <div className="akv-loading">
            <Spinner animation="border" size="sm" />
            <span>Carregando chaves...</span>
          </div>
        ) : (
          <>
            {/* Key List */}
            {vaultKeys.length === 0 && !showAddForm ? (
              <div className="akv-empty">
                <IconShieldLock size={48} />
                <span>Nenhuma chave cadastrada</span>
                <p>Adicione suas API keys para usar nos agentes</p>
              </div>
            ) : (
              <div className="akv-list">
                {Object.entries(groupedKeys).map(([provider, keys]) => {
                  const cfg = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.openai;
                  const Icon = cfg.icon;
                  return (
                    <div key={provider} className="akv-group">
                      <div className="akv-group__header">
                        <div className="akv-group__icon" style={{ background: cfg.gradient }}>
                          <Icon size={16} />
                        </div>
                        <span className="akv-group__name">{cfg.name}</span>
                        <span className="akv-group__count">{keys.length}</span>
                      </div>
                      <div className="akv-group__items">
                        {keys.map((k) => (
                          <VaultKeyItem
                            key={k.id}
                            entry={k}
                            onDelete={handleDelete}
                            onEditLabel={handleEditLabel}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Form */}
            {showAddForm && (
              <AddKeyForm
                onAdd={handleAdd}
                onCancel={() => setShowAddForm(false)}
              />
            )}

            {/* Footer */}
            <div className="akv-footer">
              <span className="akv-footer__count">
                <IconKey size={14} />
                {vaultKeys.length}/{MAX_KEYS} chaves
              </span>
              {!showAddForm && vaultKeys.length < MAX_KEYS && (
                <button className="akv-btn akv-btn--primary" onClick={() => setShowAddForm(true)}>
                  <IconPlus size={16} />
                  Adicionar chave
                </button>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default ApiKeysTab;
